import { useExportExamEventReportItemsLazyQuery, useGetAllProvincesQuery } from '@report/graphql';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Text,
} from '@shared/components';
import { ORDER_TYPE } from '@shared/graphql';
import { useSnackbar } from '@shared/snackbar';
import { dayjs, toDayRange, triggerFileDownload } from '@shared/utils';
import { useMemo } from 'react';

import { EXAM_TYPE_LABEL, REGISTRATION_TYPE_LABEL } from '../../constants';
import { useExamEventOverviewReportSearchParams } from '../../hooks';

const MAXIMUM_EXPORT_COUNT = 50000;

interface ExamEventOverviewReportExportModalProps {
  open: boolean;
  onClose: () => void;
  totalCount: number;
}

export default function ExamEventOverviewReportExportModal(
  props: Readonly<ExamEventOverviewReportExportModalProps>,
) {
  const { open, onClose, totalCount } = props;
  const { showSnackbar } = useSnackbar();

  const [
    { startDate, endDate, examCenter, registrationType, province, examType, sortBy, sortOrder },
  ] = useExamEventOverviewReportSearchParams();

  const formattedStartDate = startDate ? dayjs(startDate).format('D MMM BBBB') : '-';
  const formattedEndDate = endDate ? dayjs(endDate).format('D MMM BBBB') : '-';

  const formattedRegType =
    registrationType.map((type) => REGISTRATION_TYPE_LABEL[type]).join(', ') || '-';
  const formattedExamType = examType.map((type) => EXAM_TYPE_LABEL[type]).join(', ') || '-';

  const formattedExamCenter = examCenter.find((center) => center.toLowerCase() === 'all')
    ? 'Select All'
    : examCenter.join(', ') || '-';

  const { data: provinceData } = useGetAllProvincesQuery();
  const provinceOptions = useMemo(() => {
    const provinces = [...(provinceData?.reportGetAllProvinces?.provinces ?? [])];
    return provinces
      .sort((a, b) => a.nameTh.localeCompare(b.nameTh))
      .map((province) => ({
        label: province.nameTh,
        value: province.id,
      }));
  }, [provinceData]);

  const formattedProvince = province.find((province) => province.toLowerCase() === 'all')
    ? 'Select All'
    : province
        .map((province) => provinceOptions.find((p) => p.value === province)?.label)
        .join(', ') || '-';

  const [exportExamEventReportItems, { loading: isExporting }] =
    useExportExamEventReportItemsLazyQuery();

  const handleExport = async () => {
    const { startedAt, endedAt } = toDayRange(startDate, endDate);

    await exportExamEventReportItems({
      variables: {
        exportExamEventReportItemsInput: {
          filter: {
            examCenterCodes: examCenter.includes('all') ? null : examCenter,
            examTypes: examType,
            registrationTypes: registrationType,
            provinceIds: province.includes('all') ? null : province,
            startedAt: startedAt as Date,
            endedAt: endedAt as Date,
          },
          order: {
            field: sortBy,
            type: ORDER_TYPE[sortOrder],
          },
        },
      },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const signedUrl = data.reportExportExamEventReportItems.signedUrl;
        if (!signedUrl) {
          showSnackbar({
            message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
            variant: 'error',
          });
          onClose();
          return;
        }

        triggerFileDownload(signedUrl);
        showSnackbar({
          message: 'ส่งออกข้อมูลเรียบร้อยแล้ว',
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
          variant: 'error',
        });
        onClose();
      },
    });
  };

  const isExceedingMaximumExportCount = totalCount > MAXIMUM_EXPORT_COUNT;
  const disableExport = isExporting || isExceedingMaximumExportCount;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs'>
      <DialogTitle onClose={onClose} loading={isExporting}>
        ยืนยันการส่งออกข้อมูล
      </DialogTitle>
      <DialogContent dividers>
        <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(3) })}>
          {isExceedingMaximumExportCount ? (
            <Alert severity='error'>
              จำกัดการดาวน์โหลดสูงสุด {MAXIMUM_EXPORT_COUNT.toLocaleString('th-TH')} รายการต่อครั้ง
            </Alert>
          ) : null}
          <Text variant='body1Semibold'>สรุปข้อมูลที่ต้องการ</Text>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              Date:
            </Text>
            <Text>
              {formattedStartDate} - {formattedEndDate}
            </Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              Exam Center:
            </Text>
            <Text>{formattedExamCenter}</Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              Reg Type:
            </Text>
            <Text>{formattedRegType}</Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              Province:
            </Text>
            <Text>{formattedProvince}</Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              Exam Type:
            </Text>
            <Text>{formattedExamType}</Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              รวมทั้งหมด:
            </Text>
            <Text>{totalCount.toLocaleString('th-TH')} รายการ</Text>
          </Flex>
        </Flex>
      </DialogContent>
      <DialogActions>
        <Flex sx={(theme) => ({ gap: theme.spacing(2), justifyContent: 'flex-end' })}>
          <Button size='large' variant='outlined' color='secondary' onClick={onClose}>
            ยกเลิก
          </Button>
          <Button
            size='large'
            loading={isExporting}
            disabled={disableExport}
            onClick={handleExport}
          >
            ส่งออกข้อมูล
          </Button>
        </Flex>
      </DialogActions>
    </Dialog>
  );
}
